import { Tuple, UnknownAction, type ThunkMiddleware } from '@reduxjs/toolkit';
import { configureStore } from '@reduxjs/toolkit';
import {
  ECGReducer,
  bloodPressureReducer,
  facilityReducer,
  patientReducer,
  patientsReducer,
  userReducer,
  studyReducer,
  requestReducer,
  ppgReducer,
} from './slice';

import { errorHandler } from './middlewares';
import { notification } from 'utils/notification';
import { awsS3Bucket } from 'packages/aws-s3-bucket';
import { services } from '~/api/services';

type RootReducer = {
  user: ReturnType<typeof userReducer>;
  patient: ReturnType<typeof patientReducer>;
  ecg: ReturnType<typeof ECGReducer>;
  patients: ReturnType<typeof patientsReducer>;
  facility: ReturnType<typeof facilityReducer>;
  bloodPressure: ReturnType<typeof bloodPressureReducer>;
  study: ReturnType<typeof studyReducer>;
  requests: ReturnType<typeof requestReducer>;
  ppg: ReturnType<typeof ppgReducer>;
};

type ExtraArguments = {
  notification: typeof notification;
  awsS3Bucket: typeof awsS3Bucket;
  services: typeof services;
};

class Store {
  public instance: ReturnType<
    typeof configureStore<
      RootReducer,
      UnknownAction,
      Tuple<[ThunkMiddleware<RootReducer, UnknownAction, ExtraArguments>]>
    >
  >;

  public constructor() {
    this.instance = configureStore({
      reducer: {
        user: userReducer,
        patient: patientReducer,
        ecg: ECGReducer,
        patients: patientsReducer,
        facility: facilityReducer,
        bloodPressure: bloodPressureReducer,
        study: studyReducer,
        requests: requestReducer,
        ppg: ppgReducer,
      },
      middleware: (getDefaultMiddleware) => {
        return getDefaultMiddleware({
          thunk: {
            extraArgument: this.extraArguments,
          },
          serializableCheck: false,
        }).concat(errorHandler);
      },
    });
  }

  public get extraArguments(): ExtraArguments {
    return {
      notification,
      awsS3Bucket,
      services,
    };
  }
}

export { Store };
